<template>
  <div>
    <a-modal
      v-model="formModalVisible"
      title="Create New User"
      centered
      :afterClose="() => this.createUserForm.resetFields()"
      :maskClosable="false"
    >
      <a-form layout="vertical" id="createUserForm" :form="createUserForm" @submit="handleSubmit">
        <a-form-item label="Full Name">
          <a-input
            v-decorator="[
              'fullName',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a name!'
                  }
                ]
              }
            ]"
          />
        </a-form-item>
        <a-form-item>
          <span slot="label"
            >Username
            <a-tooltip title="Used for Login Purposes">
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-input
            v-decorator="[
              'username',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a username!',
                    whitespace: true
                  }
                ]
              }
            ]"
          />
        </a-form-item>
        <a-form-item label="Email">
          <a-input
            v-decorator="[
              'email',
              {
                rules: [
                  {
                    type: 'email',
                    message: 'Please enter a valid email!'
                  },
                  {
                    required: true,
                    message: 'Please enter an email!'
                  }
                ]
              }
            ]"
          />
        </a-form-item>
        <a-form-item label="Phone Number">
          <a-input
            v-decorator="[
              'phone',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a phone number!'
                  }
                ]
              }
            ]"
            style="width: 100%"
          >
            <a-select slot="addonBefore" v-decorator="['prefix', { initialValue: '+01' }]" style="width: 70px">
              <a-select-option value="+01">
                +01
              </a-select-option>
              <a-select-option value="+56">
                +56
              </a-select-option>
            </a-select>
          </a-input>
        </a-form-item>
        <a-form-item label="Company">
          <a-select
            v-decorator="[
              'company',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select a company!'
                  }
                ]
              }
            ]"
            :allowClear="true"
            placeholder="Select User's Company"
            :getPopupContainer="trigger => trigger.parentNode"
          >
            <a-select-option v-for="(item, index) in displayCompaniesForUsers" :key="index" :value="item">
              {{ item }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Authorized Companies">
          <a-tree-select
            v-decorator="['authorizedCompanies']"
            :allowClear="true"
            placeholder="Select Authorized Companies"
            :dropdown-style="{ maxHeight: '300px', overflow: 'auto' }"
            style="width: 100%"
            :tree-data="companyTreeData"
            tree-checkable
            :getPopupContainer="trigger => trigger.parentNode"
          />
        </a-form-item>
        <a-form-item>
          <span slot="label"
            >Access Group
            <a-tooltip title="Used to Determine Access Level">
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-radio-group
            v-decorator="[
              'userGroup',
              { initialValue: 'customer' },
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select at least one group!'
                  }
                ]
              }
            ]"
          >
            <a-radio value="admin">
              admin
            </a-radio>
            <a-radio value="customer">
              customer
            </a-radio>
            <a-radio value="developer">
              developer (I/O engineering team only)
            </a-radio>
          </a-radio-group>
        </a-form-item>
        <a-form-item label="Password">
          <a-input
            v-decorator="[
              'password',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a password!'
                  },
                  {
                    validator: validateToNextPassword
                  }
                ]
              }
            ]"
            type="password"
          />
        </a-form-item>
        <a-form-item label="Confirm Password">
          <a-input
            v-decorator="[
              'confirm',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please confirm your password!'
                  },
                  {
                    validator: compareToFirstPassword
                  }
                ]
              }
            ]"
            type="password"
            @blur="handleConfirmBlur"
          />
        </a-form-item>
      </a-form>
      <template slot="footer">
        <a-button
          form="createUserForm"
          key="create"
          type="primary"
          icon="user-add"
          html-type="submit"
          :loading="createFormButtonLoading"
        >
          Create
        </a-button>
      </template>
    </a-modal>
    <a-form class="ant-advanced-search-form" :form="userSearchForm" @submit="handleUserSearch">
      <a-row :gutter="24">
        <a-col :span="8">
          <a-form-item label="Company">
            <a-select v-decorator="['company']" :allowClear="true" placeholder="Select company">
              <a-select-option v-for="(item, index) in displayCompaniesForUsers" :key="index" :value="item">
                {{ item }}
              </a-select-option>
            </a-select>
          </a-form-item>
        </a-col>
        <a-col :span="16" :style="{ textAlign: 'right' }">
          <a-form-item>
            <a-button type="primary" html-type="submit" icon="search">
              Search Users
            </a-button>
            <a-button type="primary" @click="setFormModalVisible(true)" :style="{ marginLeft: '8px' }" icon="user-add">
              Create New User
            </a-button>
          </a-form-item>
        </a-col>
      </a-row>
    </a-form>
    <br />
    <div class="search-result-list">
      <a-table
        :columns="userColumns"
        :dataSource="handleUserData"
        :scroll="{ x: 1500, y: 1000 }"
        :loading="userDataLoading"
        :rowKey="record => record.Username"
        :pagination="{
          defaultPageSize: 10,
          showSizeChanger: true
        }"
        bordered
        @change="onTableChange"
        :expandedRowRender="usersTableExpandedRowHandler"
      >
        <template slot="footer">
          {{ this.userEntriesFound + " Users Found" }}
        </template>
        <div
          slot="filterDropdown"
          slot-scope="{ setSelectedKeys, selectedKeys, confirm, clearFilters }"
          style="padding: 8px"
        >
          <a-input
            v-ant-ref="component => (searchInput = component)"
            placeholder="Search User"
            :value="selectedKeys[0]"
            style="width: 188px; margin-bottom: 8px; display: block;"
            @change="e => setSelectedKeys(e.target.value ? [e.target.value] : [])"
            @pressEnter="confirm()"
          />
          <a-button type="primary" icon="search" size="small" style="width: 90px; margin-right: 8px" @click="confirm()">
            Search
          </a-button>
          <a-button size="small" style="width: 90px" @click="clearFilters()">
            Reset
          </a-button>
        </div>
        <a-icon
          slot="filterIcon"
          slot-scope="filtered"
          type="search"
          :style="{ color: filtered ? '#108ee9' : undefined }"
        />
        <span slot="actions" slot-scope="text, record">
          <a-popconfirm ok-text="Yes" cancel-text="No" @confirm="deleteUser(record.Username)">
            <template slot="title">
              <p style="margin:0">
                Are you sure you want to
                <span style="color:#f50">delete</span> this user?
              </p>
            </template>
            <a>Delete</a>
          </a-popconfirm>
          <span v-if="record.Enabled === true">
            <a-divider type="vertical" />
            <a-popconfirm okText="Yes" cancelText="No" @confirm="disableUser(record.Username)">
              <template slot="title">
                <p style="margin:0">
                  Are you sure you want to
                  <span style="color:#f50">disable</span> this user?
                </p>
              </template>
              <a>Disable</a>
            </a-popconfirm>
          </span>
          <span v-else>
            <a-divider type="vertical" />
            <a-popconfirm ok-text="Yes" cancel-text="No" @confirm="enableUser(record.Username)">
              <template slot="title">
                <p style="margin:0">
                  Are you sure you want to
                  <span style="color:#f50">enable</span> this user?
                </p>
              </template>
              <a>Enable</a>
            </a-popconfirm>
          </span>
          <a-divider type="vertical" />
          <a @click="editUser(record)">Edit</a>
          <a-divider type="vertical" />
          <a @click="setChangePasswordFormModal(record.Username)">Change Password</a>
        </span>
      </a-table>
    </div>
    <a-modal
      v-model="editUserModalVisible"
      title="Edit User"
      centered
      :afterClose="() => this.editUserForm.resetFields()"
    >
      <a-form layout="vertical" id="editUserForm" :form="editUserForm" @submit="saveChanges">
        <a-form-item label="Full Name">
          <a-input
            v-decorator="[
              'fullName',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a name!'
                  }
                ],
                initialValue: editUserFormInitialValues.fullName
              }
            ]"
          />
        </a-form-item>
        <a-form-item>
          <span slot="label"
            >Username
            <a-tooltip title="Used for Login Purposes">
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-input
            v-decorator="[
              'username',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a username!',
                    whitespace: true
                  }
                ],
                initialValue: editUserFormInitialValues.username
              }
            ]"
            :disabled="true"
          />
        </a-form-item>
        <a-form-item label="Email">
          <a-input
            v-decorator="[
              'email',
              {
                rules: [
                  {
                    type: 'email',
                    message: 'Please enter a valid email!'
                  },
                  {
                    required: true,
                    message: 'Please enter an email!'
                  }
                ],
                initialValue: editUserFormInitialValues.email
              }
            ]"
          />
        </a-form-item>
        <a-form-item label="Phone Number">
          <a-input
            v-decorator="[
              'phone',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a phone number!'
                  }
                ],
                initialValue: editUserFormInitialValues.phone
              }
            ]"
            style="width: 100%"
          >
            <a-select
              slot="addonBefore"
              v-decorator="['prefix', { initialValue: editUserFormInitialValues.prefix }]"
              style="width: 70px"
            >
              <a-select-option value="+01">
                +01
              </a-select-option>
              <a-select-option value="+56">
                +56
              </a-select-option>
            </a-select>
          </a-input>
        </a-form-item>
        <a-form-item label="Company">
          <a-select
            v-decorator="[
              'company',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select a company!'
                  }
                ],
                initialValue: editUserFormInitialValues.company
              }
            ]"
            :allowClear="true"
            placeholder="Select User's Company"
            :getPopupContainer="trigger => trigger.parentNode"
          >
            <a-select-option v-for="(item, index) in displayCompaniesForUsers" :key="index" :value="item">
              {{ item }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="Authorized Companies">
          <a-tree-select
            v-decorator="[
              'authorizedCompanies',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select at least one company!'
                  }
                ],
                initialValue: editUserFormInitialValues.authorizedCompanies
              }
            ]"
            :allowClear="true"
            placeholder="Select Authorized Companies"
            :dropdown-style="{ maxHeight: '300px', overflow: 'auto' }"
            style="width: 100%"
            :tree-data="companyTreeData"
            tree-checkable
            :getPopupContainer="trigger => trigger.parentNode"
          />
        </a-form-item>
        <a-form-item>
          <span slot="label"
            >Access Group
            <a-tooltip title="Used to Determine Access Level">
              <a-icon type="question-circle-o" />
            </a-tooltip>
          </span>
          <a-radio-group
            v-decorator="[
              'userGroup',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please select at least one group!'
                  }
                ],
                initialValue: editUserFormInitialValues.group
              }
            ]"
          >
            <a-radio value="admin">
              admin
            </a-radio>
            <a-radio value="customer">
              customer
            </a-radio>
            <a-radio value="developer">
              developer
            </a-radio>
          </a-radio-group>
        </a-form-item>
      </a-form>
      <template slot="footer">
        <a-button key="cancel" @click="handleCancel">
          Cancel
        </a-button>
        <a-button
          form="editUserForm"
          key="saveChanges"
          type="primary"
          html-type="submit"
          :loading="editUserFormButtonLoading"
        >
          Save Changes
        </a-button>
      </template>
    </a-modal>
    <a-modal
      v-model="changePasswordModalVisible"
      title="Set New Password"
      centered
      :afterClose="() => this.changePasswordForm.resetFields()"
    >
      <a-form layout="vertical" id="changePasswordForm" :form="changePasswordForm" @submit="changePassword">
        <a-form-item label="Username">
          <a-input
            v-decorator="[
              'username',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a username!',
                    whitespace: true
                  }
                ],
                initialValue: changePasswordFormInitialValues.username
              }
            ]"
            :disabled="true"
          />
        </a-form-item>
        <a-form-item label="New Password">
          <a-input
            v-decorator="[
              'newPassword',
              {
                rules: [
                  {
                    required: true,
                    message: 'Please enter a password!'
                  },
                  {
                    min: 8,
                    message: 'Password must be at least 8 characters.'
                  }
                ]
              }
            ]"
            placeholder="Enter new password"
          />
        </a-form-item>
      </a-form>
      <template slot="footer">
        <a-button
          form="changePasswordForm"
          key="changePassword"
          type="primary"
          html-type="submit"
          :loading="changePasswordFormButtonLoading"
        >
          Confirm
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { mapState } from "vuex";
export default {
  data() {
    return {
      createUserForm: this.$form.createForm(this),
      userSearchForm: this.$form.createForm(this),
      editUserForm: this.$form.createForm(this),
      changePasswordForm: this.$form.createForm(this),
      searchInput: null,
      confirmDirty: false,
      autoCompleteResult: [],
      userData: [],
      userDataLoading: false,
      createFormButtonLoading: false,
      editUserFormButtonLoading: false,
      changePasswordFormButtonLoading: false,
      changePasswordModalVisible: false,
      formModalVisible: false,
      editUserModalVisible: false,
      userEntriesFound: 0,
      editUserFormInitialValues: {},
      changePasswordFormInitialValues: {},
      userColumns: [
        {
          dataIndex: "Attributes[0].Value",
          title: "Name",
          align: "center",
          width: 210,
          scopedSlots: {
            filterDropdown: "filterDropdown",
            filterIcon: "filterIcon"
          },
          onFilter: (value, record) => record.Attributes[0].Value.toLowerCase().includes(value.toLowerCase()),
          onFilterDropdownVisibleChange: visible => {
            if (visible) {
              setTimeout(() => {
                this.searchInput.focus();
              }, 0);
            }
          }
        },
        {
          dataIndex: "Username",
          title: "Username",
          align: "center",
          width: 200
        },
        {
          dataIndex: "Attributes[3].Value",
          title: "Email",
          align: "center"
        },
        {
          dataIndex: "Attributes[4].Value",
          title: "Company",
          sorter: (recordOne, recordTwo) => recordOne.Attributes[4].Value.localeCompare(recordTwo.Attributes[4].Value),
          align: "center"
        },
        {
          dataIndex: "Attributes[1].Value",
          title: "Phone Number",
          align: "center"
        },
        {
          dataIndex: "group",
          title: "Access Group",
          align: "center",
          filters: [
            {
              text: "admin",
              value: "admin"
            },
            {
              text: "customer",
              value: "customer"
            },
            {
              text: "developer",
              value: "developer"
            }
          ],
          onFilter: (value, record) => record.group === value
        },
        {
          dataIndex: "lastLogin",
          title: "Last Successful Login",
          align: "center"
        },
        {
          dataIndex: "Enabled",
          title: "Account Status",
          customRender: enabled => {
            if (enabled) {
              return "Enabled";
            } else {
              return <p style="color:#f50; margin:0">Disabled</p>;
            }
          },
          align: "center",
          filters: [
            {
              text: "Enabled",
              value: "true"
            },
            {
              text: "Disabled",
              value: "false"
            }
          ],
          filterMultiple: false,
          onFilter: (value, record) => record.Enabled.toString() === value
        },
        {
          title: "Actions",
          scopedSlots: { customRender: "actions" },
          align: "center",
          width: 200
        }
      ]
    };
  },

  props: ["cognitoidentityserviceprovider", "masterCompanyMap", "AWS"],

  computed: {
    ...mapState(["PoolData", "IdentityPoolData", "UserPool", "IdToken"]),

    innnerUserColumns() {
      const innnerColumns = [
        {
          dataIndex: "company",
          title: "Authorized Company",
          align: "center"
        },
        {
          dataIndex: "depot",
          title: "Location(s)",
          customRender: depotList => {
            return depotList.sort().join(", ");
          },
          align: "center"
        },
        {
          dataIndex: "region",
          title: "Region",
          align: "center"
        },
        {
          dataIndex: "hasTransformer",
          title: "Has Tranformer",
          customRender: hasTransformer => {
            if (hasTransformer) {
              return "Yes";
            } else {
              return "No";
            }
          },
          align: "center"
        }
      ];
      return innnerColumns;
    },

    // function to display the list of companies to assign to a user.
    displayCompaniesForUsers() {
      let companyList = [];
      this.masterCompanyMap.forEach((companyObject, companyName) => {
        companyList.push(companyName);
      });
      // also add I/O Controls and BYD.
      companyList.push("I/O Controls");
      companyList.push("BYD");
      return companyList.sort();
    },

    // function to display the list of companies in the master company list.
    displayCompanies() {
      let companyList = [];
      this.masterCompanyMap.forEach((companyObject, companyName) => {
        companyList.push(companyName);
      });
      return companyList.sort();
    },

    // function to display list of available companies to authorize in a tree structure.
    companyTreeData() {
      let authorizedCompanyList = [];
      if (this.masterCompanyMap) {
        this.masterCompanyMap.forEach((valueObject, company) => {
          // only proceed if the list of depots are defined.
          if (valueObject.depots) {
            let companyName = company;
            let companyObject = {};
            var listOfLocations = valueObject.depots.sort();
            companyObject.title = companyName;
            companyObject.value = companyName;
            companyObject.key = companyName;
            companyObject.children = [];
            listOfLocations.forEach(location => {
              let locationObject = {};
              locationObject.title = location;
              locationObject.value = companyName + "-" + location;
              locationObject.key = companyName + "-" + location;
              companyObject.children.push(locationObject);
            });
            authorizedCompanyList.push(companyObject);
          }
        });
      }
      return authorizedCompanyList;
    },

    handleUserData() {
      // copy data over, and sort/filter it according to requirements.
      const data = this.userData;
      data.sort((recordOne, recordTwo) => {
        return recordOne.Attributes[0].Value.localeCompare(recordTwo.Attributes[0].Value);
      });
      return data;
    }
  },

  methods: {
    handleSubmit(e) {
      e.preventDefault();
      this.createUserForm.validateFieldsAndScroll((error, values) => {
        if (error) {
          return;
        }
        try {
          this.createFormButtonLoading = true;
          // using the user's credentials, check if they have access to edit the User Pool.
          this.AWS.config.credentials.get(credentialError => {
            // only proceed if the user has access to add users to the User Pool.
            if (!credentialError) {
              // sign up the user using the form field values.
              var signUpParams = {
                ClientId: this.PoolData.ClientId,
                Username: values.username,
                Password: values.password,
                UserAttributes: [
                  {
                    Name: "email",
                    Value: values.email
                  },
                  {
                    Name: "phone_number",
                    Value: values.prefix + values.phone
                  },
                  {
                    Name: "name",
                    Value: values.fullName
                  },
                  {
                    Name: "custom:company",
                    Value: values.company
                  },
                  {
                    Name: "custom:authorizedCompanies",
                    Value: this.generateAuthorizedCompaniesObject(values.authorizedCompanies)
                  }
                ]
              };
              this.cognitoidentityserviceprovider.signUp(signUpParams, signUpError => {
                if (signUpError) {
                  this.createFormButtonLoading = false;
                  this.$message.error("Sign Up Error: " + (signUpError.message || JSON.stringify(signUpError)));
                  return;
                }

                // once the user is signed up, add user to the appropriate group selected.
                var params = {
                  GroupName: values.userGroup,
                  UserPoolId: this.PoolData.UserPoolId,
                  Username: values.username
                };
                this.cognitoidentityserviceprovider.adminAddUserToGroup(params, async addToGroupError => {
                  if (addToGroupError) {
                    this.createFormButtonLoading = false;
                    this.$message.error("Error while adding user to the selected group: " + addToGroupError);
                    return;
                  } else {
                    let message = await this.attachPrincipalPolicy(values.username, values.password, "us-west-2");
                    this.createFormButtonLoading = false;
                    this.createUserForm.resetFields();
                    this.setFormModalVisible(false);
                    this.$success({
                      title: "User Successfully Created " + message,
                      content: (
                        <p>
                          Please update the{" "}
                          <a
                            href="https://docs.google.com/spreadsheets/d/1XugtdXoHvZBKt6FkKhLq5KT7pIKkGYzBXzxpVysoeu0/edit?usp=sharing"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            ELMS Website Accounts
                          </a>{" "}
                          spreadsheet with the new user's information.
                        </p>
                      )
                    });
                    this.loadUserData(this.userSearchForm.getFieldValue("company"));
                  }
                });
              });
            } else {
              if (credentialError.statusCode == 400) {
                this.$message.error(
                  "You are not authorized to create new users. Please contact Support Team if you think this is a mistake."
                );
              }
              // reset the form and close the modal.
              this.createFormButtonLoading = false;
              this.createUserForm.resetFields();
              this.setFormModalVisible(false);
            }
          });
        } catch (error) {
          this.$message.error("Server Error. Please refresh and try again.");
          // reset the form and close the modal.
          this.createFormButtonLoading = false;
          this.createUserForm.resetFields();
          this.setFormModalVisible(false);
        }
      });
    },

    handleUserSearch(e) {
      e.preventDefault();
      this.userSearchForm.validateFields(async (error, fieldValues) => {
        if (error) {
          return;
        }
        // console.log(fieldValues);
        // this.attachAllPrincipalPolicies();
        this.loadUserData(fieldValues.company);
      });
    },

    saveChanges(e) {
      e.preventDefault();
      this.editUserForm.validateFields((error, fieldsValue) => {
        if (error) {
          return;
        }
        try {
          this.editUserFormButtonLoading = true;
          // before editing the user's attributes, check to make sure user has access to edit the user pool / credentials need to be refreshed.
          this.AWS.config.credentials.get(credentialError => {
            if (credentialError) {
              if (credentialError.name === "NotAuthorizedException") {
                this.$message.error(
                  "You are not authorized to edit the user pool. Please contact Support Team if you think this is a mistake."
                );
              }
              return;
            } else {
              var promiseArray = [];
              var params = {
                UserPoolId: this.PoolData.UserPoolId,
                Username: fieldsValue.username,
                UserAttributes: [
                  {
                    Name: "name",
                    Value: fieldsValue.fullName
                  },
                  {
                    Name: "email",
                    Value: fieldsValue.email
                  },
                  {
                    Name: "phone_number",
                    Value: fieldsValue.prefix + fieldsValue.phone
                  },
                  {
                    Name: "custom:company",
                    Value: fieldsValue.company
                  },
                  {
                    Name: "custom:authorizedCompanies",
                    Value: this.generateAuthorizedCompaniesObject(fieldsValue.authorizedCompanies)
                  }
                ]
              };
              // create a promise to update the user attributes.
              var updateUserAttributesPromise = this.cognitoidentityserviceprovider
                .adminUpdateUserAttributes(params)
                .promise();
              promiseArray.push(updateUserAttributesPromise);
              // if the group is changed, create two promises to remove from old group and add to new group respectively.
              if (fieldsValue.userGroup != this.editUserFormInitialValues.group) {
                var removeParams = {
                  GroupName: this.editUserFormInitialValues.group,
                  UserPoolId: this.PoolData.UserPoolId,
                  Username: fieldsValue.username
                };
                var removeFromGroupPromise = this.cognitoidentityserviceprovider
                  .adminRemoveUserFromGroup(removeParams)
                  .promise();
                var addParams = {
                  GroupName: fieldsValue.userGroup,
                  UserPoolId: this.PoolData.UserPoolId,
                  Username: fieldsValue.username
                };
                var addToGroupPromise = this.cognitoidentityserviceprovider.adminAddUserToGroup(addParams).promise();
                promiseArray.push(removeFromGroupPromise);
                promiseArray.push(addToGroupPromise);
              }
              // only proceed once all the promises have returned.
              Promise.all(promiseArray)
                .then(() => {
                  this.editUserFormButtonLoading = false;
                  this.editUserForm.resetFields();
                  this.editUserModalVisible = false;
                  this.$message.success("Successfully updated user information!");
                  this.loadUserData(this.userSearchForm.getFieldValue("company"));
                })
                .catch(() => {
                  this.editUserFormButtonLoading = false;
                  this.editUserForm.resetFields();
                  this.editUserModalVisible = false;
                  this.$message.error("Error updating user attributes. Please try again later!");
                });
            }
          });
        } catch (error) {
          this.$message.error("Server Error. Please refresh and try again!");
          // reset the form and close the modal.
          this.editUserFormButtonLoading = false;
          this.editUserForm.resetFields();
          this.editUserModalVisible = false;
        }
      });
    },

    usersTableExpandedRowHandler(row) {
      var innerTableData = [];
      // iterate through the userData to find the company object for the row selected.
      this.userData.forEach(object => {
        if (row.Username == object.Username) {
          innerTableData = JSON.parse(object.Attributes[2].Value);
        }
      });
      return (
        <a-table
          columns={this.innnerUserColumns}
          dataSource={innerTableData}
          pagination={false}
          rowKey={(record, index) => index + 1}
        />
      );
    },

    loadUserData(selectedCompany) {
      this.userDataLoading = true;
      this.userData = [];
      // before listing the user pool, check to make sure user has access to view users / credentials need to be refreshed.
      this.AWS.config.credentials.get(credentialError => {
        if (credentialError) {
          if (credentialError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to view the user pool. Please contact Support Team if you think this is a mistake."
            );
          }
          this.userDataLoading = false;
          return;
        } else {
          var params = {
            UserPoolId: this.PoolData.UserPoolId,
            AttributesToGet: ["name", "phone_number", "email", "custom:authorizedCompanies", "custom:company"]
          };
          this.cognitoidentityserviceprovider.listUsers(params, (error, data) => {
            if (error) {
              this.userDataLoading = false;
              this.$message.error("Error listing users from User Pool. Refresh page to try again!");
            } else {
              // once user data is returned, create a promise for each user to retrieve the lastLogin and group information before displaying the data.
              var filteredData = [];
              if (!selectedCompany) {
                filteredData = data.Users;
              } else {
                filteredData = data.Users.filter(user => {
                  if (user.Attributes[4].Value == selectedCompany) {
                    return user;
                  }
                });
              }
              var authPromiseArray = [];
              var groupPromiseArray = [];
              filteredData.map(user => {
                var authParams = {
                  UserPoolId: this.PoolData.UserPoolId,
                  Username: user.Username,
                  MaxResults: 5
                };
                var groupParams = {
                  UserPoolId: this.PoolData.UserPoolId,
                  Username: user.Username
                };
                var authPromise = this.cognitoidentityserviceprovider.adminListUserAuthEvents(authParams).promise();
                var groupPromise = this.cognitoidentityserviceprovider.adminListGroupsForUser(groupParams).promise();
                authPromiseArray.push(authPromise);
                groupPromiseArray.push(groupPromise);
                // set the default value in case an error occurs.
                user.lastLogin = "N/A";
                user.group = "N/A";
              });
              // only proceed once all the promises have returned.
              Promise.all(authPromiseArray)
                .then(values => {
                  values.forEach(returnedObject => {
                    var username = returnedObject.$response.request.params.Username;
                    var authEvents = returnedObject.AuthEvents;
                    filteredData.forEach(user => {
                      if (user.Username === username) {
                        // check if no login attempts have been made.
                        if (authEvents === undefined || authEvents.length == 0) {
                          user.lastLogin = "No Login Attempts Found";
                        } else {
                          // iterate over the authEvents to find the most recent successful login.
                          var successfulLoginFound = false;
                          authEvents.some(event => {
                            if (event.EventType == "SignIn" && event.EventResponse == "Pass") {
                              user.lastLogin = event.CreationDate.toString();
                              successfulLoginFound = true;
                              return true;
                            }
                          });
                          // if no successful attempt found in the last 5 events, display message accordingly.
                          if (!successfulLoginFound) {
                            user.lastLogin = "No Recent Successful Login Found";
                          }
                        }
                      }
                    });
                  });
                })
                .catch(() => {
                  // in case any error occurs, set the value of userData with default lastLogin values.
                  this.userData = filteredData;
                  this.userEntriesFound = filteredData.length;
                  this.userDataLoading = false;
                });
              Promise.all(groupPromiseArray)
                .then(values => {
                  values.forEach(returnedObject => {
                    var username = returnedObject.$response.request.params.Username;
                    var groups = returnedObject.Groups;
                    filteredData.forEach(user => {
                      if (user.Username === username) {
                        user.group = groups[0].GroupName;
                      }
                    });
                  });

                  // finally, set the value of userData and userEntriesFound.
                  this.userData = filteredData;
                  this.userEntriesFound = filteredData.length;
                  this.userDataLoading = false;
                })
                .catch(() => {
                  // in case any error occurs, set the value of userData with default lastLogin values.
                  this.userData = filteredData;
                  this.userEntriesFound = filteredData.length;
                  this.userDataLoading = false;
                });
            }
          });
        }
      });
    },

    attachPrincipalPolicy(username, password) {
      return new Promise(resolve => {
        try {
          let authParams = {
            AuthFlow: "ADMIN_USER_PASSWORD_AUTH",
            ClientId: this.PoolData.ClientId,
            UserPoolId: this.PoolData.UserPoolId,
            AuthParameters: {
              USERNAME: username,
              PASSWORD: password
            }
          };
          this.cognitoidentityserviceprovider.adminInitiateAuth(authParams, (authError, authData) => {
            if (authError) {
              resolve("but Unable to Attach IoT Policies.");
            } else {
              var idToken = authData.AuthenticationResult.IdToken;
              this.AWS.config.credentials = new this.AWS.CognitoIdentityCredentials({
                IdentityPoolId: this.IdentityPoolData.DeveloperIdentityPoolId,
                Logins: {
                  [this.IdentityPoolData.LoginARN]: idToken
                }
              });
              this.AWS.config.credentials.clearCachedId();
              this.AWS.config.credentials.get(error => {
                if (error) {
                  resolve("but Unable to Attach IoT Policies.");
                } else {
                  var principal = this.AWS.config.credentials.identityId;
                  this.AWS.config.credentials = new this.AWS.CognitoIdentityCredentials({
                    IdentityPoolId: this.IdentityPoolData.DeveloperIdentityPoolId,
                    Logins: {
                      [this.IdentityPoolData.LoginARN]: this.IdToken
                    }
                  });
                  this.AWS.config.credentials.clearCachedId();
                  var attachPromiseArray = [];
                  let iot_client_us = new this.AWS.Iot({ region: "us-west-2" });
                  let iot_client_sa = new this.AWS.Iot({ region: "sa-east-1" });
                  let attachParams = {
                    policyName: "ELMSWebsite_PublishSubscribe",
                    principal: principal
                  };
                  let attachPromise_us = iot_client_us.attachPrincipalPolicy(attachParams).promise();
                  let attachPromise_sa = iot_client_sa.attachPrincipalPolicy(attachParams).promise();
                  attachPromiseArray.push(attachPromise_us);
                  attachPromiseArray.push(attachPromise_sa);
                  Promise.all(attachPromiseArray)
                    .then(() => {
                      resolve("and Successfully Attached IoT Policies.");
                    })
                    .catch(() => {
                      resolve("but Unable to Attach IoT Policies.");
                    });
                }
              });
            }
          });
        } catch (error) {
          resolve("but Unable to Attach IoT Policies.");
        }
      });
    },

    setFormModalVisible(value) {
      this.formModalVisible = value;
    },

    handleCancel() {
      this.editUserForm.resetFields();
      this.editUserModalVisible = false;
    },

    handleConfirmBlur(e) {
      const value = e.target.value;
      this.confirmDirty = this.confirmDirty || !!value;
    },

    compareToFirstPassword(rule, value, callback) {
      const form = this.createUserForm;
      if (value && value !== form.getFieldValue("password")) {
        callback("Passwords do not match!");
      } else {
        callback();
      }
    },

    onTableChange(pagination, filters, sorter, { currentDataSource }) {
      // update the current number of rows in the table.
      this.userEntriesFound = currentDataSource.length;
    },

    validateToNextPassword(rule, value, callback) {
      const form = this.createUserForm;
      if (value && this.confirmDirty) {
        form.validateFields(["confirm"], { force: true });
      }
      callback();
    },

    editUser(record) {
      // set the initial values of the form before displaying it.
      this.editUserFormInitialValues.fullName = record.Attributes[0].Value;
      this.editUserFormInitialValues.username = record.Username;
      this.editUserFormInitialValues.email = record.Attributes[3].Value;
      this.editUserFormInitialValues.phone = record.Attributes[1].Value.slice(3, record.Attributes[1].Value.length);
      this.editUserFormInitialValues.prefix = record.Attributes[1].Value.slice(0, 3);
      this.editUserFormInitialValues.company = record.Attributes[4].Value;
      this.editUserFormInitialValues.group = record.group;
      var initialAuthorizedCompanies = [];
      var authorizedCompanies = JSON.parse(record.Attributes[2].Value);
      authorizedCompanies.forEach(company => {
        company.depot.forEach(location => {
          initialAuthorizedCompanies.push(company.company + "-" + location);
        });
      });
      this.editUserFormInitialValues.authorizedCompanies = initialAuthorizedCompanies;
      this.editUserModalVisible = true;
    },

    deleteUser(username) {
      // before deleting the user, check to make sure user has access to edit the user pool / credentials need to be refreshed.
      this.AWS.config.credentials.get(credentialError => {
        if (credentialError) {
          if (credentialError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to edit the user pool. Please contact Support Team if you think this is a mistake."
            );
          } else {
            this.$message.error("Error retrieving developer credentials. Please try again!");
          }
          return;
        } else {
          this.userDataLoading = true;
          var params = {
            UserPoolId: this.PoolData.UserPoolId,
            Username: username
          };
          this.cognitoidentityserviceprovider.adminDeleteUser(params, error => {
            if (error) {
              this.userDataLoading = false;
              this.$message.error("Error deleting the user. Please try again later!");
            } else {
              this.$message.success("Successfully deleted user!");
              this.loadUserData(this.userSearchForm.getFieldValue("company"));
            }
          });
        }
      });
    },

    enableUser(username) {
      // before enabling the user, check to make sure user has access to edit the user pool / credentials need to be refreshed.
      this.AWS.config.credentials.get(credentialError => {
        if (credentialError) {
          if (credentialError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to edit the user pool. Please contact Support Team if you think this is a mistake."
            );
          } else {
            this.$message.error("Error retrieving developer credentials. Please try again!");
          }
          return;
        } else {
          this.userDataLoading = true;
          var params = {
            UserPoolId: this.PoolData.UserPoolId,
            Username: username
          };
          this.cognitoidentityserviceprovider.adminEnableUser(params, error => {
            if (error) {
              this.userDataLoading = false;
              this.$message.error("Error enabling the user. Please try again later!");
            } else {
              this.$message.success("Successfully enabled user!");
              this.loadUserData(this.userSearchForm.getFieldValue("company"));
            }
          });
        }
      });
    },

    disableUser(username) {
      // before disabling the user, check to make sure user has access to edit the user pool / credentials need to be refreshed.
      this.AWS.config.credentials.get(credentialError => {
        if (credentialError) {
          if (credentialError.name === "NotAuthorizedException") {
            this.$message.error(
              "You are not authorized to edit the user pool. Please contact Support Team if you think this is a mistake."
            );
          } else {
            this.$message.error("Error retrieving developer credentials. Please try again!");
          }
          return;
        } else {
          this.userDataLoading = true;
          var params = {
            UserPoolId: this.PoolData.UserPoolId,
            Username: username
          };
          this.cognitoidentityserviceprovider.adminDisableUser(params, error => {
            if (error) {
              this.userDataLoading = false;
              this.$message.error("Error disabling the user. Please try again later!");
            } else {
              this.$message.success("Successfully disabled user!");
              this.loadUserData(this.userSearchForm.getFieldValue("company"));
            }
          });
        }
      });
    },

    generateAuthorizedCompaniesObject(rawData) {
      var authorizedCompanies = [];
      try {
        rawData.forEach(location => {
          const company = location.slice(0, location.indexOf("-"));
          // if authorizedCompanies is empty, create the first company object.
          if (authorizedCompanies.length == 0) {
            let companyObject = {};
            companyObject.company = company;
            companyObject.depot = [];
            companyObject.depot.push(location.slice(location.indexOf("-") + 1, location.length));
            // add region and hasTranformer to the companyObject as well.
            companyObject.hasTransformer = this.masterCompanyMap.get(company).hasTransformer;
            companyObject.region = this.masterCompanyMap.get(company).region;
            authorizedCompanies.push(companyObject);
          }
          // else, iterate to find if company object already exists.
          else {
            var companyObjectFound = false;
            authorizedCompanies.forEach(existingCompanyObject => {
              // if company object already exists, add the depot to the list.
              if (existingCompanyObject.company == company) {
                companyObjectFound = true;
                existingCompanyObject.depot.push(location.slice(location.indexOf("-") + 1, location.length));
              }
            });
            // else, create a new company object and add it to authorizedCompanies.
            if (!companyObjectFound) {
              let companyObject = {};
              companyObject.company = company;
              companyObject.depot = [];
              companyObject.depot.push(location.slice(location.indexOf("-") + 1, location.length));
              // add region and hasTranformer to the companyObject as well.
              companyObject.hasTransformer = this.masterCompanyMap.get(company).hasTransformer;
              companyObject.region = this.masterCompanyMap.get(company).region;
              authorizedCompanies.push(companyObject);
            }
          }
        });
        return JSON.stringify(authorizedCompanies);
      } catch (error) {
        return JSON.stringify(authorizedCompanies);
      }
    },

    setChangePasswordFormModal(username) {
      this.changePasswordFormInitialValues.username = username;
      this.changePasswordModalVisible = true;
    },

    changePassword(event) {
      event.preventDefault();
      this.changePasswordForm.validateFields((error, values) => {
        if (error) {
          return;
        }
        // before disabling the user, check to make sure user has access to edit the user pool / credentials need to be refreshed.
        this.AWS.config.credentials.get(credentialError => {
          if (credentialError) {
            if (credentialError.name === "NotAuthorizedException") {
              this.$message.error(
                "You are not authorized to change user passwords. Please contact Support Team if you think this is a mistake."
              );
            } else {
              this.$message.error("Error retrieving developer credentials. Please try again!");
            }
            return;
          } else {
            this.changePasswordFormButtonLoading = true;
            var params = {
              Password: values.newPassword,
              Permanent: true,
              UserPoolId: this.PoolData.UserPoolId,
              Username: values.username
            };

            this.cognitoidentityserviceprovider.adminSetUserPassword(params, error => {
              if (error) {
                this.$message.error("Error changing the password: " + error.message);
                this.changePasswordFormButtonLoading = false;
              } else {
                this.changePasswordFormButtonLoading = false;
                this.changePasswordForm.resetFields();
                this.changePasswordModalVisible = false;
                this.$success({
                  title: "Password Successfully Changed",
                  content: (
                    <p>
                      Please update the{" "}
                      <a
                        href="https://docs.google.com/spreadsheets/d/1XugtdXoHvZBKt6FkKhLq5KT7pIKkGYzBXzxpVysoeu0/edit?usp=sharing"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        ELMS Website Accounts
                      </a>{" "}
                      spreadsheet with the user's new password information.
                    </p>
                  )
                });
              }
            });
          }
        });
      });
    }

    // attachAllPrincipalPolicies() {
    //   var attachPromiseArray = [];
    //   var principals = [
    //     "us-west-2:32c54ebe-7f7f-4ab8-83ff-9dcec8c22316",
    //     "us-west-2:3dc7f8ae-a592-4ce6-ab93-606ed9abccda",
    //     "us-west-2:4ce3a724-bbb3-484a-921a-9d754b86432c",
    //     "us-west-2:7fde5b4c-3545-430c-ad34-1f6c2fd069ab",
    //     "us-west-2:af0fc745-51c3-4c8c-856a-0d98b22ac461",
    //     "us-west-2:f8ce9639-2532-481b-af2d-18cf2058f1d3"
    //   ];
    //   let iot_client_us = new this.AWS.Iot({ region: "us-west-2" });
    //   let iot_client_sa = new this.AWS.Iot({ region: "sa-east-1" });
    //   principals.forEach(principal => {
    //     let attachParams = {
    //       policyName: "ELMSWebsite_PublishSubscribe",
    //       principal: principal
    //     };
    //     let attachPromise_us = iot_client_us.attachPrincipalPolicy(attachParams).promise();
    //     let attachPromise_sa = iot_client_sa.attachPrincipalPolicy(attachParams).promise();
    //     attachPromiseArray.push(attachPromise_us);
    //     attachPromiseArray.push(attachPromise_sa);
    //   });
    //   Promise.all(attachPromiseArray)
    //     .then(() => {
    //       console.log("and Successfully Attached IoT Policies.");
    //     })
    //     .catch(() => {
    //       console.log("but Unable to Attach IoT Policies.");
    //     });
    // }
  }
};
</script>

<style lang="less" scoped>
.ant-form-item {
  margin-bottom: 5px;
}

.a-button {
  margin: 0 auto;
  display: block;
}
</style>
